import { Typography } from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MemoryIcon from "@mui/icons-material/Memory";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

// Tool Types Configuration
export const TOOL_TYPES = {
  ITERATOR: "iterator",
  TRANSLATOR: "translator",
  REDACTION: "redaction",
  CHATBOX: "chatbot",
  SUPPLIERDEMAND: "supplierDemandConstants",
  SUPPLY_CONSTANTS: "supplyConstants",
  DEMAND_CONSTANTS: "demandConstants",
};

// Base instruction data
const createInstructionData = (toolType) => {
  const baseInstructions = [
    {
      id: 1,
      title: "Upload Your Excel File",
      icon: <CloudUploadIcon size={28} />,
    },
    {
      id: 2,
      title: "AI Processing",
      icon: <MemoryIcon size={28} />,
    },
    {
      id: 3,
      title: "Download Results",
      icon: <FileDownloadIcon size={28} />,
    },
  ];

  // Customize based on tool type
  switch (toolType) {
    case TOOL_TYPES.ITERATOR:
      return [
        {
          ...baseInstructions[0],
          description:
            "Your file can contain multiple columns, but the script only reads the column labeled",
          code: "Prompt",
          details:
            "Each row should include a question, instruction, or description.",
        },
        {
          ...baseInstructions[1],
          description:
            "This tool processes each prompt by sending it to OpenAI's language model to generate a relevant response. You can test the tool beforehand to preview the type of output you can expect.",
          details:
            "Before starting the process, you have the option to run your file using either a single-prompt or multi-prompt approach. To achieve the best results, choose the option that is most appropriate for your uploaded file.",
        },
        {
          ...baseInstructions[2],
          description:
            "You’ll receive a new Excel file containing all your original columns, plus one (or more) columns:",
          outputs: [
            { label: "Prompt", desc: "your original input" },
            { label: "Response", desc: "the raw, AI-generated output" },
            {
              label: "Additional",
              desc: "any further columns generated as a result of your prompt",
            },
          ],
        },
      ];

    case TOOL_TYPES.TRANSLATOR:
      return [
        {
          ...baseInstructions[0],
          description:
            "Your file can have multiple columns, but the tool only reads the column labeled:",
          code: "Translate",
          details:
            "Each row should contain text in any language. If it’s already in English, it will be left unchanged.",
        },
        {
          ...baseInstructions[1],
          description:
            "The tool sends each entry in the 'Translate' column to OpenAI's language model, which returns the English translation.",
        },
        {
          ...baseInstructions[2],
          description:
            "You’ll receive a new Excel file containing all your original columns, plus one extra column with the translation.",
          outputs: [
            { label: "Translate", desc: "The original text from your file" },
            {
              label: "Translated_Text",
              desc: "The AI-generated English translation",
            },
          ],
        },
      ];

    case TOOL_TYPES.REDACTION:
      return [
        {
          ...baseInstructions[0],
          description:
            "Your file can contain multiple columns, but the tool only reads the column labeled",
          code: "Redaction",
          details:
            "Each row should contain text that needs to be reviewed for sensitive information.",
        },
        {
          ...baseInstructions[1],
          description:
            "The tool analyzes each piece of content to identify and redact sensitive or confidential information using AI.",
        },
        {
          ...baseInstructions[2],
          description: "You'll receive a new Excel file with redacted content:",
          outputs: [
            { label: "Redaction", desc: "your original text" },
            {
              label: "Redacted_Text",
              desc: "the content with sensitive information removed",
            },
          ],
        },
      ];

    case TOOL_TYPES.SUPPLY_CONSTANTS:
      return [
        {
          ...baseInstructions[0],
          description:
            "Your file must include the following columns for the tool to run correctly:",
          code: "L1, L2, Sector, Country",
        },
        {
          ...baseInstructions[1],
          description:
            "The tool uses your L1, L2, Sector, and Country values to calculate supply-side constants via OpenAI's language model.",
          details:
            "Processing time depends on the number of rows in your file.",
        },
        {
          ...baseInstructions[2],
          description:
            "You'll receive a single Excel file named 'Supply_Constants_Output' containing two sheets:",
          outputs: [
            {
              label: "Constants",
              desc: "The calculated supply constants for each L1/L2/Sector/Country combination",
            },
            {
              label: "Checks",
              desc: "Validation results flagging any rows that need review",
            },
          ],
        },
      ];

    case TOOL_TYPES.DEMAND_CONSTANTS:
      return [
        {
          ...baseInstructions[0],
          description:
            "Your file must include the following columns for the tool to run correctly:",
          code: "L1, L2, L3, Sector",
          details:
            "Only Excel files in .xlsx format are supported for processing.",
        },
        {
          ...baseInstructions[1],
          description:
            "The tool uses your L1, L2, L3 values to calculate demand-side constants via OpenAI's language model.",
          details:
            "Processing time depends on the number of rows in your file.",
        },
        {
          ...baseInstructions[2],
          description:
            "You'll receive a single Excel file named 'New_Constants' containing four sheets:",
          outputs: [
            {
              label: "Constants",
              desc: "The calculated demand constants for each L1/L2/L3 combination",
            },
            {
              label: "Inflation",
              desc: "Inflation-adjusted figures related to demand",
            },
            {
              label: "Carbon",
              desc: "Carbon impact metrics associated with the demand data",
            },
            {
              label: "Porter 5 Forces",
              desc: "Competitive analysis breakdown using Porter's Five Forces framework",
            },
          ],
        },
      ];

    default:
      return baseInstructions;
  }
};

// Export specific instruction data
export const instructionIteratorData = createInstructionData(
  TOOL_TYPES.ITERATOR,
);
export const instructionTranslatorData = createInstructionData(
  TOOL_TYPES.TRANSLATOR,
);
export const instructionRedactionData = createInstructionData(
  TOOL_TYPES.REDACTION,
);
export const instructionSupplyConstantsData = createInstructionData(
  TOOL_TYPES.SUPPLY_CONSTANTS,
);
export const instructionDemandConstantsData = createInstructionData(
  TOOL_TYPES.DEMAND_CONSTANTS,
);

export const getInstructionData = (toolType) => {
  return createInstructionData(toolType);
};

// Tool information
const toolInfoMap = {
  [TOOL_TYPES.ITERATOR]: {
    title: "Iteration Process",
    description:
      "This tool helps you generate AI-powered responses for each prompt or question listed in your Excel file.",
  },
  [TOOL_TYPES.TRANSLATOR]: {
    title: "Translation Process",
    description:
      "This tool enables the automatic conversion of text from one language to another.",
  },
  [TOOL_TYPES.REDACTION]: {
    title: "Redaction Process",
    description:
      "This tool assists in editing or preparing documents for release by removing or hiding sensitive or confidential information.",
  },
  [TOOL_TYPES.CHATBOX]: {
    title: "Chat Box Investigator",
    description:
      "The Chat Box Investigator helps monitor, analyze, and review chat interactions on your website. It can be used to track user queries, identify common issues, and ensure quality support.",
  },
  [TOOL_TYPES.SUPPLIERDEMAND]: {
    title: "Supplier Demand Constants",
    description:
      "This tool generates supply and demand constants for suppliers.",
  },
  [TOOL_TYPES.SUPPLY_CONSTANTS]: {
    title: "Supply Constants",
    description:
      "Upload a file with L1, L2, Sector, and Country columns to generate supply-side constants.",
  },
  [TOOL_TYPES.DEMAND_CONSTANTS]: {
    title: "Demand Constants",
    description:
      "Upload a file with L1, L2, L3, and Sector columns to generate demand-side constants.",
  },
};

// Generic info component
export const ToolInfo = ({ toolType, customDescription }) => {
  const toolInfo = toolInfoMap[toolType];

  if (!toolInfo) {
    return null;
  }

  return (
    <Typography variant="body1" sx={{ mb: "1rem" }}>
      {customDescription || toolInfo.description}
    </Typography>
  );
};

// Backward compatibility exports
export const IteratorInfo = () => <ToolInfo toolType={TOOL_TYPES.ITERATOR} />;
export const TranslatorInfo = () => (
  <ToolInfo toolType={TOOL_TYPES.TRANSLATOR} />
);
export const RedactionInfo = () => <ToolInfo toolType={TOOL_TYPES.REDACTION} />;
export const ChatBotInfo = () => <ToolInfo toolType={TOOL_TYPES.CHATBOX} />;
export const SupplierDemandConstantsInfo = () => (
  <ToolInfo toolType={TOOL_TYPES.SUPPLIERDEMAND} />
);
